/// <amd-module name="Repos/Views/Branches/Components/BranchesTree/BranchesTree" />
import { EnhancedGitRef } from "Data/EnhancedGitRef";
import * as React from "react";
import { branchNameToRef } from "Repos/Common/Util/Ref";
import { AsyncSparseTreeLoadingCell } from "Repos/SparseTree/AsyncSparseTreeLoadingCell";
import { SparseTreeNode } from "Repos/SparseTree/SparseTreeProvider.Types";
import * as Resources from "Repos/Views/Branches/Resources";
import { IObservableLikeValue, ObservableCollection, ObservableLike } from "VSS/Core/Observable";
import { Card } from "VSSUI/Card";
import { IMenuItem } from "VSSUI/Menu";
import { renderLoadingCell, SimpleTableCell, TableColumnLayout } from "VSSUI/Table";
import { ITreeColumn, ITreeRowDetails, Tree, TreeRow } from "VSSUI/TreeEx";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import * as BranchColumns from "../Columns/BranchColumns";
import { IBranchesTreeProps } from "./BranchesTree.Props";

function getColumns(
    props: IBranchesTreeProps,
    treeRef: React.RefObject<Tree<EnhancedGitRef>>,
    addContributedMenuItems: (branchName: string, item: EnhancedGitRef | undefined, collection: ObservableCollection<IMenuItem>) => void
): ITreeColumn<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>>[] {
    const branchNameToPolicyUrl = (branchName: string) =>
        props.routingService.getBranchPolicyUrl(props.repositoryName, props.projectName, branchNameToRef(branchName));

    return [
        BranchColumns.NameColumn(props.branchProvider.pathHasPolicy, branchNameToPolicyUrl),
        BranchColumns.CommitColumn,
        BranchColumns.AuthorColumn,
        BranchColumns.AuthoredDateColumn,
        BranchColumns.BehindAheadColumn(props.branchProvider.getCompareBranch()),
        BranchColumns.StatusColumn,
        BranchColumns.PullRequestColumn,
        BranchColumns.FavoriteColumn(props.onSetFavoriteBranch, props.onSetFavoriteFolder, props.branchProvider.getFolderFavorite),
        BranchColumns.ActionsColumn(
            addContributedMenuItems,
            props.isUserMember,
            props.onCheckPermission,
            props.onCreate,
            props.onDelete,
            props.onLock,
            props.onUnlock,
            props.onSetAsCompare,
            props.onSetAsDefault,
            props.onShowSecurityDialog,
            branchNameToPolicyUrl,
            treeRef
        )
    ];
}

export const BranchesTree: React.FunctionComponent<IBranchesTreeProps> = props => {
    const { branchProvider, addContributedMenuItems, onBranchRowActivate } = props;
    const [treeRef] = React.useState(React.createRef<Tree<EnhancedGitRef>>());
    const [columns, setColumns] = React.useState(() => getColumns(props, treeRef, addContributedMenuItems));
    React.useEffect(() => setColumns(getColumns(props, treeRef, addContributedMenuItems)), [branchProvider, addContributedMenuItems]);

    return (
        <Card className="bolt-table-card bolt-card-white" contentProps={{ contentPadding: false }}>
            <Tree
                ariaLabel={Resources.BranchesTable}
                itemProvider={branchProvider.baseProvider}
                columns={columns}
                onActivate={(event, row) => {
                    const sparseNode = ObservableLike.getValue(row.data.underlyingItem.data);
                    sparseNode && sparseNode.kind === "item" && onBranchRowActivate(sparseNode.data);
                }}
                onToggle={branchProvider.baseProvider.toggle}
                ref={treeRef as any}
                selectableText={true}
                tableBreakpoints={BranchColumns.StandardBreakpoints}
                renderLoadingRow={(
                    rowIndex: number,
                    rowDetails: ITreeRowDetails<IObservableLikeValue<SparseTreeNode<EnhancedGitRef> | undefined>>
                ) => {
                    return (
                        <TreeRow key={rowIndex} className="bolt-list-row-loading" details={rowDetails} index={rowIndex}>
                            <AsyncSparseTreeLoadingCell
                                columnIndex={0}
                                columnLayout={TableColumnLayout.singleLinePrefix}
                                fetchData={() =>
                                    rowDetails.data.parentItem &&
                                    ObservableLike.getValue(rowDetails.data.parentItem.underlyingItem.data) &&
                                    // Made safe by the type check the line above
                                    branchProvider.baseProvider.loadNextPage(
                                        rowDetails.data.parentItem as ITreeItemEx<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>>
                                    )
                                }
                                treeItem={rowDetails.data}
                            />
                            {SimpleTableCell({
                                columnIndex: 1,
                                children: renderLoadingCell()
                            })}
                            {SimpleTableCell({
                                columnIndex: 2,
                                children: renderLoadingCell()
                            })}
                            {SimpleTableCell({
                                columnIndex: 3,
                                children: renderLoadingCell()
                            })}
                            {SimpleTableCell({
                                columnIndex: 4,
                                children: renderLoadingCell()
                            })}
                            {SimpleTableCell({
                                columnIndex: 5,
                                children: renderLoadingCell()
                            })}
                            {SimpleTableCell({
                                columnIndex: 6,
                                children: renderLoadingCell()
                            })}
                        </TreeRow>
                    );
                }}
            />
        </Card>
    );
};
